import {
    Component,
    ChangeDetectionStrategy,
    ViewEncapsulation,
    OnDestroy,
    Input,
    EventEmitter,
    Output,
    OnChanges,
    SimpleChanges,
    ViewChild,
    ElementRef,
    AfterViewInit
} from '@angular/core';
import {
    FitBoundsOptions,
    FullscreenControl,
    LngLatBounds,
    LngLatLike,
    Map,
    MapboxOptions,
    Marker,
    NavigationControl,
    Popup,
    PopupOptions
} from 'mapbox-gl';
import { PlatformService } from '@core/services/platform.service';
import { ServerTokensService } from '@core/services/server-tokens.service';
import { config, dkBounds } from '@features/map/config';
import { FeatureCollection, GeoJsonProperties, Point } from 'geojson';
import isEqual from 'lodash.isequal';
import { MapFeature, PotientialCtaPayload } from '@features/map/map.models';

@Component({
    selector: 'app-mapbox',
    templateUrl: './mapbox.component.html',
    styleUrls: ['./mapbox.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: true
})
export class MapboxComponent implements AfterViewInit, OnDestroy, OnChanges {
    private map: Map;
    private markers: Marker[] = [];
    private popups: Popup[] = [];

    @Input()
    private featureCollection: FeatureCollection<Point, GeoJsonProperties>;

    @ViewChild('mapContainer')
    private readonly mapContainer: ElementRef<HTMLDivElement>;

    @Output()
    public featureClick = new EventEmitter<MapFeature>();

    @Output()
    public ctaClick = new EventEmitter<PotientialCtaPayload>();

    constructor(
        private platformService: PlatformService,
        private serverTokenService: ServerTokensService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.featureCollection.isFirstChange() || this.platformService.isServer) {
            return;
        }

        const featureChanges = changes.featureCollection;

        if (!isEqual(featureChanges.previousValue, featureChanges.currentValue)) {
            this.setMarkers(featureChanges.currentValue.features);
            this.fitBounds(featureChanges.currentValue.features);
        }
    }

    ngAfterViewInit(): void {
        if (this.platformService.isServer || !this.mapContainer?.nativeElement) {
            return;
        }

        this.map = new Map({ ...this.getConfiguration(), container: this.mapContainer.nativeElement });

        this.map.on('load', () => {
            const features = this.featureCollection?.features;

            if (!features?.length) {
                return;
            }

            this.setMarkers(features);

            this.map.addControl(new NavigationControl({ showCompass: false }), 'top-right');
            this.map.addControl(new FullscreenControl(), 'top-left');

            // Fit bounds to Denmark no matter map size
            this.map.fitBounds(dkBounds);
        });
    }

    ngOnDestroy(): void {
        if (this.map) {
            this.map.remove();
        }
    }

    private setMarkers(features: MapFeature[], markerColor = '#D20A11'): void {
        if (this.markers.length) {
            this.markers.forEach(marker => marker.remove());
        }

        if (this.popups.length) {
            this.popups.forEach(popup => popup.remove());
        }

        features.forEach(feature => {
            const marker = new Marker({
                color: markerColor
            })
                .setLngLat(feature.geometry.coordinates as LngLatLike)
                .setPopup(this.getPopup(feature.properties?.html, feature))
                .addTo(this.map);

            marker.getElement().addEventListener('click', () => this.onFeatureClick(marker, feature));
            this.markers.push(marker);
        });
    }

    private getPopup(htmlContent: string, feature: MapFeature, popupOptions?: PopupOptions): Popup {
        const node = document.createElement('div');
        node.innerHTML = htmlContent;

        const potientialCtas = node.querySelectorAll('button, a');

        if (potientialCtas?.length) {
            potientialCtas.forEach(cta =>
                cta.addEventListener('click', (event: Event) => {
                    const type = cta instanceof HTMLButtonElement ? 'button' : 'a';
                    this.ctaClick.emit({ event, type, feature });
                })
            );
        }

        const popup = new Popup({
            offset: 25,
            maxWidth: '180px',
            className: 'mapbox-popup',
            ...popupOptions
        }).setDOMContent(node);

        this.popups.push(popup);
        return popup;
    }

    private fitBounds(features: MapFeature[], fitBoundsOptions?: FitBoundsOptions) {
        if (!features.length) {
            return;
        }

        const bounds = new LngLatBounds();

        features.forEach(({ geometry }) => {
            bounds.extend([geometry.coordinates[0], geometry.coordinates[1]]);
        });

        const zoom = features.length === 1 ? 15 : null;

        this.map.fitBounds(bounds, { padding: 50, ...(zoom ? { zoom } : {}), ...fitBoundsOptions });
    }

    private getConfiguration(): MapboxOptions {
        if (this.platformService.isLocal) {
            return config;
        }

        const { mapboxToken, mapboxStyle } = this.serverTokenService;

        return {
            ...config,
            accessToken: mapboxToken,
            style: mapboxStyle
        };
    }

    private onFeatureClick(marker: Marker, feature: MapFeature): void {
        this.featureClick.emit(feature);
        this.fitBounds([feature], { offset: [0, 160] });
    }
}
